import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableHead,
  TableRow, TextField, Button, MenuItem, FormControl, InputLabel,
  Select, IconButton, CircularProgress, Alert, Stack, Chip
} from '@mui/material';
import { Download, FilterList, EventNote } from '@mui/icons-material';
import api from '../../api/axios';
import { format, parseISO, startOfMonth, endOfMonth } from 'date-fns';

const TimesheetManager = () => {
  const [logs, setLogs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFormData] = useState({
    start_date: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
    end_date: format(new Date(), 'yyyy-MM-dd'),
    employee_id: ''
  });
  const [message, setMessage] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [empRes, logsRes] = await Promise.all([
        api.get('/admin/employees'),
        api.get('/admin/team/timesheets', { params: filters })
      ]);
      setEmployees(empRes.data || []);
      setLogs(logsRes.data || []);
    } catch (err) {
      console.error('Failed to fetch timesheet data:', err);
      setMessage({ type: 'error', text: 'Failed to load timesheets.' });
    }
    setLoading(false);
  };

  const handleFilterChange = (e) => {
    setFormData({ ...filters, [e.target.name]: e.target.value });
  };

  const applyFilters = () => {
    fetchData();
  };

  const exportCSV = () => {
    if (logs.length === 0) return;
    
    const headers = ['Employee', 'Event', 'Timestamp', 'Job ID', 'Notes'];
    const rows = logs.map(log => [
      log.employee_name,
      log.event_type,
      format(parseISO(log.timestamp), 'yyyy-MM-dd HH:mm:ss'),
      log.job_id || '-',
      log.notes || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(r => r.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `timesheets_${filters.start_date}_to_${filters.end_date}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading && logs.length === 0) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      {message && <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>{message.text}</Alert>}
      
      <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid #E2E8F0' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          <TextField
            label="Start Date"
            type="date"
            name="start_date"
            value={filters.start_date}
            onChange={handleFilterChange}
            InputLabelProps={{ shrink: true }}
            size="small"
            fullWidth
          />
          <TextField
            label="End Date"
            type="date"
            name="end_date"
            value={filters.end_date}
            onChange={handleFilterChange}
            InputLabelProps={{ shrink: true }}
            size="small"
            fullWidth
          />
          <FormControl fullWidth size="small">
            <InputLabel>Employee</InputLabel>
            <Select
              name="employee_id"
              value={filters.employee_id}
              onChange={handleFilterChange}
              label="Employee"
            >
              <MenuItem value="">All Employees</MenuItem>
              {employees.map(emp => (
                <MenuItem key={emp.id} value={emp.id}>{emp.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button 
            variant="contained" 
            startIcon={<FilterList />} 
            onClick={applyFilters}
            sx={{ borderRadius: 2, textTransform: 'none', px: 4 }}
          >
            Filter
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<Download />} 
            onClick={exportCSV}
            disabled={logs.length === 0}
            sx={{ borderRadius: 2, textTransform: 'none', px: 4 }}
          >
            Export
          </Button>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid #E2E8F0' }}>
        <Table>
          <TableHead sx={{ bgcolor: '#F8FAFC' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Employee</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Event</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Time</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Job</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Notes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                  <EventNote sx={{ fontSize: 48, color: 'text.secondary', mb: 1, opacity: 0.2 }} />
                  <Typography color="text.secondary">No time entries for this period</Typography>
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log) => (
                <TableRow key={log.id} hover>
                  <TableCell fontWeight={600}>{log.employee_name}</TableCell>
                  <TableCell>
                    <Chip 
                      label={log.event_type.replace('_', ' ').toUpperCase()} 
                      size="small" 
                      color={log.event_type.includes('clock') ? 'primary' : 'secondary'}
                      variant="outlined"
                      sx={{ fontWeight: 700, fontSize: '0.7rem' }}
                    />
                  </TableCell>
                  <TableCell>{format(parseISO(log.timestamp), 'MMM d, h:mm a')}</TableCell>
                  <TableCell>{log.job_id ? `#${log.job_id}` : '-'}</TableCell>
                  <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {log.notes || '-'}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default TimesheetManager;
